import React, { useState } from 'react';
import Table from '@components/table';
import styles from './styles.module.scss';
import Typography from '@components/common/typography';
import moment from 'moment/moment';
import { useNavigate } from 'react-router-dom';
import { routes } from '@const/routes';
import { useAmlAlerts } from '@hooks/api/useAmlAlerts';
import { TAmlAlertLite } from '@xeppt/xeppt-sdk/types';
import Status from '@components/common/status';
import { getAmlSeverityColor, getAmlAlertStatusColor } from '@utils/index';
import Button from '@components/common/button';
import Drawer from '@components/drawer';
import Select from '@components/common/select';
import RangeDatePicker from '@components/range_date_picker';

const alertStatusItems = [
    { value: '', label: 'All' },
    { value: 'open', label: 'Open' },
    { value: 'reviewed', label: 'Reviewed' },
    { value: 'escalated', label: 'Escalated' },
    { value: 'dismissed', label: 'Dismissed' },
    { value: 'str_filed', label: 'STR Filed' }
];

const alertSeverityItems = [
    { value: '', label: 'All' },
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' }
];

const alertTypeItems = [
    { value: '', label: 'All' },
    { value: 'sanctions', label: 'Sanctions' },
    { value: 'pep', label: 'PEP' },
    { value: 'velocity', label: 'Velocity' },
    { value: 'threshold', label: 'Threshold' },
    { value: 'structuring', label: 'Structuring' },
    { value: 'geo', label: 'Geo' },
    { value: 'behavioral', label: 'Behavioral' },
    { value: 'attempted', label: 'Attempted' },
    { value: 'sanctions_block', label: 'Sanctions Block' },
    { value: 'fraud', label: 'Fraud' }
];

const orderItems = [
    { value: 'desc', label: 'Newest first' },
    { value: 'asc', label: 'Oldest first' }
];

const AmlAlertsLayout = () => {
    const { alerts, alertsTotal, alertsFilter, refetchAlerts, handleChangeAlertsFilter } =
        useAmlAlerts({ isInitialAlerts: true });
    const navigate = useNavigate();
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    const columns = [
        {
            label: 'ID',
            key: 'id'
        },
        {
            label: 'Type',
            key: 'alert_type'
        },
        {
            label: 'Severity',
            key: 'severity',
            render: (value: string) => (
                <Status variant={getAmlSeverityColor(value)}>{value || '-'}</Status>
            )
        },
        {
            label: 'Status',
            key: 'status',
            render: (value: string) => (
                <Status variant={getAmlAlertStatusColor(value)}>{value || '-'}</Status>
            )
        },
        {
            label: 'Party',
            key: 'party_id'
        },
        {
            label: 'Amount',
            key: 'amount',
            render: (_: string, data: TAmlAlertLite) =>
                data.amount ? `${data.amount} ${data.currency || ''}`.trim() : '-'
        },
        {
            label: 'Created',
            key: 'created_at',
            render: (value: string) =>
                value ? moment(value).format('MM.DD.YYYY HH:mm:ss') : '-'
        },
        {
            label: 'Detail',
            key: 'id',
            render: (value: string) => (
                <button
                    className="link"
                    onClick={() => navigate(`${routes.aml_alerts}/${value}`)}>
                    View
                </button>
            )
        }
    ];

    return (
        <div className={styles.wrapper}>
            <div className={styles.header}>
                <div className={styles.text_wrapper}>
                    <Typography variant="h4">AML Alerts</Typography>
                    <Typography>{alertsTotal} records found</Typography>
                </div>
                <div className={styles.actions}>
                    <Button
                        size="medium"
                        variant="primary"
                        leftIcon="filter_bars"
                        onClick={() => setIsFilterOpen(true)}>
                        Filter
                    </Button>
                </div>
            </div>
            <Table
                //@ts-ignore
                columns={columns}
                rows={alerts}
                totalRows={alertsTotal}
                onLoadData={() => refetchAlerts({ isNew: false, filter: alertsFilter })}
                emptyDescription="Alerts list is empty"
            />
            <Drawer
                className={styles.drawer}
                isOpen={isFilterOpen}
                onClose={() => setIsFilterOpen(false)}>
                <Typography className={styles.title} variant="h5">
                    Filter
                </Typography>
                <Select
                    full
                    label="Status"
                    value={alertsFilter.status ?? ''}
                    items={alertStatusItems}
                    onChange={(val) =>
                        handleChangeAlertsFilter({
                            status: val ? (val as typeof alertsFilter.status) : undefined
                        })
                    }
                />
                <Select
                    full
                    label="Severity"
                    value={alertsFilter.severity ?? ''}
                    items={alertSeverityItems}
                    onChange={(val) =>
                        handleChangeAlertsFilter({
                            severity: val ? (val as typeof alertsFilter.severity) : undefined
                        })
                    }
                />
                <Select
                    full
                    label="Alert Type"
                    value={alertsFilter.alertType ?? ''}
                    items={alertTypeItems}
                    onChange={(val) =>
                        handleChangeAlertsFilter({
                            alertType: val ? (val as typeof alertsFilter.alertType) : undefined
                        })
                    }
                />
                <RangeDatePicker
                    full
                    label="Date range"
                    values={[alertsFilter.from || null, alertsFilter.to || null]}
                    onChange={(val) =>
                        handleChangeAlertsFilter({
                            from: val?.[0] ? new Date(val[0]).toISOString() : undefined,
                            to: val?.[1] ? new Date(val[1]).toISOString() : undefined
                        })
                    }
                    isDisableFeature
                />
                <Select
                    full
                    label="Sort order"
                    value={alertsFilter.order}
                    items={orderItems}
                    onChange={(val) =>
                        handleChangeAlertsFilter({ order: val as 'asc' | 'desc' })
                    }
                />
            </Drawer>
        </div>
    );
};

export default AmlAlertsLayout;
